// Libraries
import { Grid, Icon, Typography } from "@mui/material";
import clsx from "clsx";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";

// Components
import LabeledFormInput from "@/Layouts/components/SmartAddressFormSection/LabeledFormInput";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";

// Helpers
import { anyValidSearchInventoryInput, sortDropdownOptions } from "../../../helpers";

// Constants
import { DESIRED_VEHICLE_TAB, REQUIRED_FIELD_CLASSES } from "../../../constants";

// Types
import { TType } from "../../../types";

// Context
import { IDesiredVehicle } from "@/DataPages/context/crm/interfaces";

// Icons
import ArrowDown from "@/assets/icons/svg/chevron-down.svg";
import ArrowUp from "@/assets/icons/svg/chevron-up.svg";

// Hooks
import useVehicleOptions from "../../../hooks/useVehicleOptions";

type TSearchInventoryProps = {
	states: {
		typeState: [string, (value: string) => void];
		vinState: [string, (value: string) => void];
		yearState: [string, (value: string) => void];
		makeState: [string, (value: string) => void];
		modelState: [string, (value: string) => void];
		styleState: [string, (value: string) => void];
		stockNumberState: [string, (value: string) => void];
	};
	isLoadingSearch: boolean;
	displayTable: boolean;
	isSearchDone: boolean;
	hasNextPage: boolean;
	disabled: boolean;
	setDisplayTable: (value: boolean) => void;
	onSearch: (param: IDesiredVehicle) => void;
};

export default function SearchInventory(props: Readonly<TSearchInventoryProps>) {
	const { states, isSearchDone, isLoadingSearch, displayTable, hasNextPage, disabled, onSearch, setDisplayTable } = props;

	const { makeState, modelState, stockNumberState, styleState, typeState, vinState, yearState } = states;

	const [type, setType] = typeState;
	const [vin, setVin] = vinState;
	const [year, setYear] = yearState;
	const [make, setMake] = makeState;
	const [model, setModel] = modelState;
	const [style, setStyle] = styleState;
	const [stockNumber, setStockNumber] = stockNumberState;

	const {
		make: { data: makeOptions = [], isLoading: isLoadingMakeOptions },
		model: { data: modelOptions = [], isLoading: isLoadingModelOptions },
		trim: { data: trimOptions = [], isLoading: isLoadingTrimOptions },
		year: { data: yearOptions = [], isLoading: isLoadingYearsOptions },
	} = useVehicleOptions(make, model, true);

	function cleanModel() {
		setModel("");
		cleanTrim();
	}

	function cleanTrim() {
		setStyle("");
		setYear("");
	}

	return (
		<>
			<Grid container rowSpacing={1.5} spacing={3} className="!mb-3">
				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Stock Type"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(DESIRED_VEHICLE_TAB.stockTypes)}
								selected={type}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => setType((value as unknown as TType)?.type ?? "")}
								inputProps={{
									placeholder: "Stock Type",
								}}
								clearable
								disabled={disabled}
							/>
						}
					/>
				</Grid>
			</Grid>

			<Grid container rowSpacing={1.5} spacing={3} className="!mb-3">
				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Stock Number"
						formInput={
							<SimpleTextField
								editable
								outlined
								inputProps={{ maxLength: 8 }}
								text={stockNumber}
								placeholder={"Stock Number"}
								onFocusOut={(value = "") => setStockNumber(value)}
								disabled={disabled || isLoadingSearch}
							/>
						}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="VIN"
						formInput={
							<SimpleTextField
								inputProps={{ maxLength: 17, className: "uppercase" }}
								editable
								outlined
								text={vin}
								placeholder={"VIN"}
								onFocusOut={(value = "") => setVin(value)}
								disabled={disabled || isLoadingSearch}
								error={Boolean(vin)}
								classes={Boolean(vin) ? REQUIRED_FIELD_CLASSES : ""}
							/>
						}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Make"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(makeOptions)}
								selected={make}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => {
									setMake((value as unknown as TType)?.type ?? "");
									cleanModel();
								}}
								inputProps={{
									placeholder: "Make",
								}}
								clearable
								loading={isLoadingMakeOptions}
								disabled={disabled || isLoadingSearch}
							/>
						}
					/>
				</Grid>
			</Grid>

			<Grid container rowSpacing={1.5} spacing={3} className="!mb-3">
				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Model"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(modelOptions)}
								selected={model}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => {
									setModel((value as unknown as TType)?.type ?? "");
									cleanTrim();
								}}
								inputProps={{
									placeholder: "Model",
								}}
								clearable
								loading={isLoadingModelOptions}
								disabled={disabled || isLoadingSearch}
							/>
						}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Style"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(trimOptions)}
								selected={style}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => setStyle((value as unknown as TType)?.type ?? "")}
								inputProps={{
									placeholder: "Style",
								}}
								clearable
								loading={isLoadingTrimOptions}
								disabled={disabled || isLoadingSearch}
							/>
						}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Year"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={yearOptions}
								selected={year}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => setYear((value as unknown as TType)?.type ?? "")}
								inputProps={{
									placeholder: "Year",
								}}
								clearable
								loading={isLoadingYearsOptions}
								disabled={disabled || isLoadingSearch}
							/>
						}
					/>
				</Grid>
			</Grid>

			<Grid container>
				<Grid item xs={4} alignItems={"center"} display="flex" justifyContent={"flex-start"}>
					<ShowSearchResults
						disabled={disabled}
						display={isSearchDone && !hasNextPage}
						displayTable={displayTable}
						setDisplayTable={setDisplayTable}
					/>
				</Grid>
				<Grid item xs={8} alignItems={"end"} display="flex" justifyContent={"flex-end"}>
					<Button
						classes={clsx({
							"!normal-case !font-sans !font-[400] !text-[14px] !min-h-[30px] !rounded-[6px] h-[30px]": true,
							"!bg-primary z-[1300]": true,
							"!bg-disabled !text-white":
								disabled ||
								isLoadingSearch ||
								!anyValidSearchInventoryInput({ make, model, style, stockNumber, type, vin, year }),
						})}
						disabled={
							disabled ||
							isLoadingSearch ||
							!anyValidSearchInventoryInput({ make, model, style, stockNumber, type, vin, year })
						}
						variant="contained"
						text="Search Inventory"
						onClick={() => onSearch({ type, vin, year, make, model, style, stockNumber })}
					/>
				</Grid>
			</Grid>
		</>
	);
}

type TShowSearchResultsProps = {
	disabled: boolean;
	display: boolean;
	displayTable: boolean;
	setDisplayTable: (value: boolean) => void;
};

function ShowSearchResults(props: Readonly<TShowSearchResultsProps>) {
	const { display, displayTable, disabled, setDisplayTable } = props;

	if (!display) {
		return null;
	}

	return (
		<Typography
			component={"label"}
			onClick={() => !disabled && setDisplayTable(!displayTable)}
			className="!text-default !font-sans !text-[14.5px] !font-normal cursor-pointer z-[1300]"
		>
			{`${displayTable ? "Hide" : "Show"} Search Results`}
			<Icon className="!m-auto !text-default !text-[12px] !ml-2 !inline-flex">
				<img src={!displayTable ? ArrowDown : ArrowUp} />
			</Icon>
		</Typography>
	);
}
